import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCart } from "../../../contexts/CartContext";

const AddToCartShop = ({ product, onClose }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split("-")[0];
  const { cart, handleAddToCart } = useCart();
  const [selectedOptions, setSelectedOptions] = useState({});
  const [globalQuantity, setGlobalQuantity] = useState(1);

  const priceTypeKey =
    {
      1: "hour",
      2: "person",
      3: "day",
      4: "unit",
      5: "fixed",
    }[product.priceTypeId] || "unit";

  const isPerUnit = priceTypeKey === "unit";
  const inventoryMode =
    product.productInventoryLimits?.inventoryMode || "unlimited";
  const totalAvailable =
    product.productInventoryLimits?.totalQuantity || Infinity;
  const isUnlimited = inventoryMode === "unlimited";
  const isFixedInventory = inventoryMode === "fixed";

  const getSelectedConfiguration = () => {
    const config = product.productConfigurations?.[0];
    const selectedIndex = selectedOptions[config?.id];
    return config?.values?.[selectedIndex];
  };

  const getQuantityInCart = () => {
    if (!cart || cart.length === 0) return 0;

    const selectedOption = getSelectedConfiguration();
    if (!selectedOption) return 0;

    const match = cart.find(
      (item) =>
        item.id === product.id && item.selectedOptionId === selectedOption.id
    );

    return match?.quantity || 0;
  };

  const getAvailableQuantity = () => {
    const selectedVal = getSelectedConfiguration();
    const quantityInCart = getQuantityInCart();

    if (selectedVal?.quantity != null) {
      return Math.max(0, selectedVal.quantity - quantityInCart);
    }

    if (isFixedInventory) {
      return Math.max(0, totalAvailable - quantityInCart);
    }

    return Infinity;
  };

  const calculateTotalPrice = () => {
    const base = product.price || 0;
    const surplus =
      product.productConfigurations?.reduce((sum, config) => {
        const selectedIndex = selectedOptions?.[config.id];
        const selectedVal = config?.values?.[selectedIndex];
        return sum + (selectedVal?.price || 0);
      }, 0) || 0;
    return ((base + surplus) * globalQuantity).toFixed(2);
  };

  const handleOptionSelect = (optionId, index) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [optionId]: index,
    }));
  };

  const handleAdd = () => {
    const selectedVal = getSelectedConfiguration();
    const payload = {
      ...product,
      quantity: globalQuantity,
      totalPrice: parseFloat(calculateTotalPrice()),
      OptionSurplus: selectedVal?.price || 0,
      OptionName: selectedVal
        ? lang === "fr"
          ? selectedVal.nameFr
          : selectedVal.nameEn
        : null,
      selectedOptionId: selectedVal?.id || null,
    };
    onClose(payload);
  };

  useEffect(() => {
    if (
      product.productConfigurations?.length > 0 &&
      Object.keys(selectedOptions).length === 0
    ) {
      const config = product.productConfigurations[0];
      const firstAvailableIndex = config.values.findIndex(
        (val) => val.quantity > 0
      );
      if (firstAvailableIndex !== -1) {
        setSelectedOptions({
          [config.id]: firstAvailableIndex,
        });
      }
    }
  }, [product]);

  const availableQty = getAvailableQuantity();
  const allOptionsOutOfStock =
    product.productConfigurations?.[0]?.values?.every(
      (val) => val.quantity === 0
    ) ?? false;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 text-primary">
      <div className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6 relative">
        <button
          onClick={() => onClose(null)}
          className="absolute top-4 right-4 text-gray-500 hover:text-black"
        >
          ✕
        </button>

        <h2 className="text-xl font-bold mb-2">
          {lang === "fr" ? product.nameFr : product.nameEn}
        </h2>
        <p className="text-gray-600 text-sm mb-4">
          {lang === "fr" ? product.descriptionFr : product.descriptionEn}
        </p>

        <div className="mb-4">
          <p className="font-semibold">{t("cart.basePrice")}</p>
          <p>${product.price.toFixed(2)}</p>
        </div>

        {product.productConfigurations?.map((option) => {
          const selectedIndex = selectedOptions?.[option.id];
          return (
            <div key={option.id} className="mb-4">
              <p className="font-semibold capitalize mb-2">
                {t("modal." + option.type)}
              </p>
              <div className="flex flex-wrap gap-2">
                {option.values.map((val, i) => {
                  const isSelected = selectedIndex === i;
                  const isDisabled = val.quantity === 0;
                  const isColorType = option.type === "color";
                  const colorCode = val.color || "#ccc";

                  return (
                    <div
                      key={val.id}
                      title={isDisabled ? t("cart.outOfStock") : ""}
                      onClick={() =>
                        !isDisabled && handleOptionSelect(option.id, i)
                      }
                      className={`relative flex flex-col items-center ${
                        isDisabled ? "cursor-not-allowed" : "cursor-pointer"
                      }`}
                      style={isColorType ? { width: "36px" } : {}}
                    >
                      {val.price > 0 && (
                        <div className="absolute -top-3 text-xs bg-black text-white px-2 py-0.5 rounded-full shadow">
                          +{val.price.toFixed(2)}$
                        </div>
                      )}
                      {isColorType ? (
                        <div
                          className={`w-8 h-8 rounded-full mb-1 relative ${
                            isSelected
                              ? "ring-2 ring-primary"
                              : "border-2 border-gray-500"
                          }`}
                          style={{ backgroundColor: colorCode }}
                        >
                          {isDisabled && (
                            <div className="absolute w-full h-0.5 bg-red-600 top-1/2 left-0 transform -translate-y-1/2 rotate-45" />
                          )}
                        </div>
                      ) : (
                        <div
                          className={`px-3 py-1 rounded-full border text-sm max-w-[200px] text-center whitespace-nowrap ${
                            isSelected
                              ? "border-muted bg-muted text-white"
                              : "border-gray-300 text-gray-800"
                          }`}
                        >
                          {lang === "fr" ? val.nameFr : val.nameEn}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {option.values.every((val) => val.quantity === 0) && (
                <p className="mt-2 text-sm font-medium text-red-700 bg-red-100 px-3 py-1 rounded shadow inline-block">
                  {t("cart.outOfStock")}
                </p>
              )}
            </div>
          );
        })}

        {isPerUnit && !allOptionsOutOfStock && (
          <div className="mb-4">
            <label className="block font-semibold mb-1">
              {t("cart.quantity", { label: t(`priceTypes.unit`) })}
            </label>

            {Number.isFinite(availableQty) && availableQty > 0 ? (
              <select
                value={globalQuantity}
                onChange={(e) => setGlobalQuantity(parseInt(e.target.value))}
                className="border px-3 py-1 rounded"
              >
                {Array.from({ length: availableQty + 1 }, (_, i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-red-600">
                {t("cart.outOfStock") || "Aucune quantité disponible"}
              </p>
            )}
          </div>
        )}

        <div className="mt-6 text-right flex justify-between items-center">
          <p className="text-lg font-semibold">
            Total:{" "}
            <span className="text-primary">${calculateTotalPrice()}</span>
          </p>
          <button
            onClick={handleAdd}
            disabled={availableQty === 0 || globalQuantity === 0}
            className={`px-6 py-2 rounded-full transition ${
              availableQty === 0 || globalQuantity === 0
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-primary text-white hover:bg-primary/90"
            }`}
          >
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToCartShop;
